import React, { useEffect, memo, useMemo } from "react";
import {
  FileText,
  Code,
  Award,
  Globe,
  ArrowUpRight,
  Sparkles,
  UserCheck,
} from "lucide-react";
import AOS from "aos";
import "aos/dist/aos.css";
import ProjectsSection from "../Components/ProjectsSection";

/**
 * Section heading — using <h2> here because <h1> lives in Home.jsx.
 * Correct heading hierarchy: H1 (Home) → H2 (About) → H3 (sub-sections).
 */
const Header = memo(() => (
  <div className="text-center lg:mb-8 mb-2 px-[5%]">
    <div className="relative inline-block group">
      <h2
        className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a855f7]"
        data-aos="zoom-in-up"
        data-aos-duration="600"
      >
        About Me
      </h2>
    </div>
    <p
      className="flex flex-wrap items-center justify-center max-w-2xl gap-2 mx-auto mt-2 text-base text-gray-400 sm:text-lg"
      data-aos="zoom-in-up"
      data-aos-duration="800"
    >
      <Sparkles className="w-5 h-5 text-purple-400" aria-hidden="true" />
      Crafting code, shaping the future.
      <Sparkles className="w-5 h-5 text-purple-400" aria-hidden="true" />
    </p>
  </div>
));
Header.displayName = 'Header';

const ProfileImage = memo(() => (
  <div className="flex items-center justify-center p-0 py-2 pb-2 sm:p-12 sm:py-0 sm:pb-0 lg:justify-end">
    <div className="relative group" data-aos="fade-up" data-aos-duration="1000">
      {/* Decorative gradient rings — hidden from screen readers */}
      <div className="absolute -inset-6 opacity-[25%] z-0 hidden sm:block" aria-hidden="true">
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-violet-600 via-indigo-500 to-purple-600 blur-2xl animate-spin-slower" />
        <div className="absolute inset-0 rounded-full opacity-50 bg-gradient-to-l from-fuchsia-500 via-rose-500 to-pink-600 blur-2xl animate-pulse-slow" />
        <div className="absolute inset-0 rounded-full opacity-50 bg-gradient-to-t from-blue-600 via-cyan-500 to-teal-400 blur-2xl animate-float" />
      </div>
      <div className="relative">
        <div className="w-[min(18rem,78vw)] h-[min(18rem,78vw)] sm:w-80 sm:h-80 rounded-full overflow-hidden shadow-[0_0_40px_rgba(120,119,198,0.3)] transform transition-all duration-700 group-hover:scale-105">
          <div className="absolute inset-0 z-20 transition-all duration-700 border-4 rounded-full border-white/20 group-hover:border-white/40 group-hover:scale-105" aria-hidden="true" />

          {/* Overlay effects — desktop only */}
          <div className="absolute inset-0 z-10 hidden transition-opacity duration-700 bg-gradient-to-b from-black/20 via-transparent to-black/40 group-hover:opacity-0 sm:block" aria-hidden="true" />
          <div className="absolute inset-0 z-10 hidden transition-opacity duration-700 opacity-0 bg-gradient-to-t from-purple-500/20 via-transparent to-blue-500/20 group-hover:opacity-100 sm:block" aria-hidden="true" />

          <img
            src="/Photo.jpeg"
            alt="Avansh Yadav — Full Stack Developer and GenAI Engineer"
            className="object-cover w-full h-full transition-all duration-700 group-hover:scale-110 group-hover:rotate-2"
            loading="lazy"
            decoding="async"
            width="320"
            height="320"
          />

          {/* Shine effects — desktop only */}
          <div className="absolute inset-0 z-20 hidden transition-all duration-700 opacity-0 group-hover:opacity-100 sm:block" aria-hidden="true">
            <div className="absolute inset-0 transition-transform duration-1000 transform -translate-x-full bg-gradient-to-tr from-transparent via-white/20 to-transparent group-hover:translate-x-full" />
            <div className="absolute inset-0 transition-transform duration-1000 delay-100 transform translate-y-full bg-gradient-to-bl from-transparent via-white/10 to-transparent group-hover:-translate-y-full" />
            <div className="absolute inset-0 transition-transform duration-700 scale-0 border-8 rounded-full border-white/10 group-hover:scale-100 animate-pulse-slow" />
          </div>
        </div>
      </div>
    </div>
  </div>
));
ProfileImage.displayName = 'ProfileImage';

const AboutPage = () => {
  useEffect(() => {
    AOS.init({ once: false });
  }, []);

  return (
    <div
      className="h-auto pb-[10%] text-gray-900 dark:text-white overflow-hidden px-[5%] sm:px-[5%] lg:px-[10%] mt-10 sm:mt-0 transition-colors duration-300"
      id="About"
    >
      <Header />
      <div className="relative w-full pt-8 mx-auto sm:pt-12">
        <div className="flex flex-col-reverse items-center gap-10 lg:grid lg:grid-cols-2 lg:gap-16">
          <div className="w-full min-w-0 space-y-6 text-center lg:text-left">

            {/* H3 used here — sub-section under H2 "About Me" */}
            <h3
              className="text-3xl font-bold sm:text-4xl lg:text-5xl"
              data-aos="fade-right"
              data-aos-duration="1000"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a855f7]">
                Hello, I'm
              </span>
              <span
                className="block mt-2 text-gray-800 dark:text-gray-200 transition-colors duration-300"
                data-aos="fade-right"
                data-aos-duration="1300"
              >
                Avansh Yadav
              </span>
            </h3>

            {/* Professional bio — rich keyword content for E-E-A-T */}
            <p
              className="pb-4 text-base leading-relaxed text-left text-gray-600 dark:text-gray-400 sm:text-justify sm:text-lg lg:text-xl sm:pb-0 transition-colors duration-300"
              data-aos="fade-right"
              data-aos-duration="1500"
            >
              Hi, I'm{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                Avansh Yadav
              </span>
              , a passionate{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a855f7] font-semibold">
                Full Stack Developer
              </span>{" "}
              and{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#6366f1] to-[#a855f7] font-semibold">
                Generative AI Engineer
              </span>{" "}
              currently pursuing B.E. at the{" "}
              <span className="font-medium">
                Army Institute of Technology, Pune
              </span>
              .
              <br />
              <br />
              I specialize in building end-to-end intelligent web applications
              that seamlessly integrate modern technologies with cutting-edge AI
              capabilities. My technical stack includes{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                MERN/MEAN stack
              </span>
              ,{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                RESTful APIs
              </span>
              ,{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                microservices architecture
              </span>
              , and AI frameworks like{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                GPT-4
              </span>
              ,{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                LangChain
              </span>
              , and{" "}
              <span className="font-medium text-gray-800 dark:text-gray-200">
                TensorFlow
              </span>
              .
              <br />
              <br />
              My expertise spans{" "}
              <span className="text-purple-600 dark:text-purple-400 font-medium">
                cloud deployment (AWS/Azure)
              </span>
              ,{" "}
              <span className="text-purple-600 dark:text-purple-400 font-medium">
                DevOps practices
              </span>
              ,{" "}
              <span className="text-purple-600 dark:text-purple-400 font-medium">
                database optimization
              </span>
              , and{" "}
              <span className="text-purple-600 dark:text-purple-400 font-medium">
                machine learning pipelines
              </span>
              . Currently exploring{" "}
              <span className="text-purple-500 font-semibold">
                Retrieval Augmented Generation (RAG)
              </span>
              ,{" "}
              <span className="text-purple-500 font-semibold">
                Vector Databases
              </span>
              ,{" "}
              <span className="text-purple-500 font-semibold">LLM Fine-tuning</span>
              , and{" "}
              <span className="text-purple-500 font-semibold">MLOps</span>. 🚀
            </p>

            <div className="flex flex-col items-center w-full gap-4 lg:flex-row lg:items-start lg:gap-4 lg:px-0">
              <a
                href="/Avansh_Yadav_Resume.pdf"
                download="Avansh_Yadav_Resume.pdf"
                className="w-full lg:w-auto"
                aria-label="Download Avansh Yadav's Resume PDF"
              >
                <button
                  data-aos="fade-up"
                  data-aos-duration="800"
                  className="w-full px-4 lg:w-auto sm:px-6 py-2 sm:py-3 rounded-lg bg-gradient-to-r from-[#6366f1] to-[#a855f7] text-white font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center lg:justify-start gap-2 shadow-lg hover:shadow-xl group"
                  type="button"
                >
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" /> Download CV
                </button>
              </a>
              <a href="#experience" className="w-full lg:w-auto" aria-label="View work experience section">
                <button
                  data-aos="fade-up"
                  data-aos-duration="1000"
                  className="w-full px-4 lg:w-auto sm:px-6 py-2 sm:py-3 rounded-lg border border-[#a855f7]/50 text-[#a855f7] font-medium transition-all duration-300 hover:scale-105 flex items-center justify-center lg:justify-start gap-2 hover:bg-[#a855f7]/10 group"
                  type="button"
                >
                  <Code className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" /> View Experience
                </button>
              </a>
            </div>
          </div>
          <ProfileImage />
        </div>
      </div>

      {/* Education & Achievements Section (E-E-A-T) */}
      <div className="mt-16 sm:mt-24 grid md:grid-cols-2 gap-8 md:gap-12 relative z-10" data-aos="fade-up">
        {/* Education Timeline */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 hover:border-purple-500/20 transition-all duration-300 text-left">
          <h4 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <span className="p-2 bg-indigo-500/10 rounded-lg text-indigo-400">🎓</span>
            Education
          </h4>
          <div className="space-y-6">
            <div className="relative pl-6 border-l border-indigo-500/30">
              <div className="absolute w-3.5 h-3.5 bg-indigo-500 rounded-full -left-[7px] top-1.5 border-4 border-[#030014]" />
              <span className="text-xs font-semibold text-indigo-400 tracking-wider">2024 — 2028</span>
              <h5 className="text-base font-bold text-white mt-1">Army Institute of Technology, Pune</h5>
              <p className="text-sm text-gray-400">B.E. in Automation and Robotics Engineering</p>
            </div>
            
            <div className="relative pl-6 border-l border-indigo-500/30">
              <div className="absolute w-3.5 h-3.5 bg-indigo-500/50 rounded-full -left-[7px] top-1.5 border-4 border-[#030014]" />
              <span className="text-xs font-semibold text-gray-400 tracking-wider">2023 — 2024</span>
              <h5 className="text-base font-bold text-white mt-1">Matrix Sikar</h5>
              <p className="text-sm text-gray-400">12th Class (CBSE) | Aggregate: 79.2%</p>
            </div>

            <div className="relative pl-6 border-l border-indigo-500/30">
              <div className="absolute w-3.5 h-3.5 bg-indigo-500/30 rounded-full -left-[7px] top-1.5 border-4 border-[#030014]" />
              <span className="text-xs font-semibold text-gray-400 tracking-wider">2021 — 2022</span>
              <h5 className="text-base font-bold text-white mt-1">Orange County School</h5>
              <p className="text-sm text-gray-400">10th Class (CBSE) | Aggregate: 90%</p>
            </div>
          </div>
        </div>

        {/* Achievements & Milestones */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 hover:border-purple-500/20 transition-all duration-300 text-left">
          <h4 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <span className="p-2 bg-yellow-500/10 rounded-lg text-yellow-400">🏆</span>
            Honours & Achievements
          </h4>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="text-yellow-400 mt-0.5">✔</span>
              <div>
                <span className="font-bold text-white block">Qualified JEE Mains & JEE Advanced (2024)</span>
                <span className="text-xs text-gray-400">Successfully qualified one of the most competitive engineering entrance exams in India.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-yellow-400 mt-0.5">✔</span>
              <div>
                <span className="font-bold text-white block">FSOC 2nd Rank (2024)</span>
                <span className="text-xs text-gray-400">Ranked 2nd in the FSOC web development challenge.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-yellow-400 mt-0.5">✔</span>
              <div>
                <span className="font-bold text-white block">SIH 2024 Internal Round Participant</span>
                <span className="text-xs text-gray-400">Participated with a working prototype for the Smart India Hackathon.</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-yellow-400 mt-0.5">✔</span>
              <div>
                <span className="font-bold text-white block">SIP State Prodigy (Sept 2019)</span>
                <span className="text-xs text-gray-400">State-level recognition for exceptional cognitive and mental calculation skills.</span>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* Leadership & Additional Information */}
      <div className="mt-8 grid md:grid-cols-2 gap-8 md:gap-12 relative z-10" data-aos="fade-up">
        {/* Leadership & Positions of Responsibility */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 hover:border-purple-500/20 transition-all duration-300 text-left">
          <h4 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <span className="p-2 bg-blue-500/10 rounded-lg text-blue-400">👥</span>
            Positions of Responsibility
          </h4>
          <div className="space-y-4">
            <div className="relative pl-6 border-l border-blue-500/30">
              <div className="absolute w-3.5 h-3.5 bg-blue-500 rounded-full -left-[7px] top-1.5 border-4 border-[#030014]" />
              <span className="text-xs font-semibold text-blue-400 tracking-wider">2024 — Present</span>
              <h5 className="text-base font-bold text-white mt-1">Class Representative (CR)</h5>
              <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                Appointed as Class Representative at AIT, coordinating communications between university staff and a cohort of 60+ students. Developed leadership, time management, active collaboration, and systematic problem-solving skills.
              </p>
            </div>
          </div>
        </div>

        {/* AI & Search Engines Q&A Section */}
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8 hover:border-purple-500/20 transition-all duration-300 text-left">
          <h4 className="text-xl md:text-2xl font-bold text-white mb-6 flex items-center gap-3">
            <span className="p-2 bg-purple-500/10 rounded-lg text-purple-400">🔍</span>
            AI Search Q&A (AI-Readiness)
          </h4>
          <div className="space-y-4">
            <div>
              <h5 className="text-sm font-bold text-purple-300">Q: Who is Avansh Yadav?</h5>
              <p className="text-xs text-gray-400 mt-1">
                Avansh Yadav is an Automation and Robotics student at Army Institute of Technology, Pune, and a Full Stack and Gen AI Developer. He specializes in React, Node.js, WebSockets, Three.js, and integrating LLMs for real-world automation applications.
              </p>
            </div>
            <div>
              <h5 className="text-sm font-bold text-purple-300">Q: What projects did Avansh Yadav build?</h5>
              <p className="text-xs text-gray-400 mt-1">
                Avansh designed the Smart India Hackathon 2024 article portal prototype and Smart Wash Tech (an intelligent campus laundry real-time slot booking system), in addition to working on advanced vector databases, serverless data pipelines, and collaborative workspace systems.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Projects preview section */}
      <ProjectsSection />
    </div>
  );
};

export default memo(AboutPage);
